import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { Check, X, Trash2, Upload, Sparkles, Plus, Users, BookOpen, MessageSquare, Lightbulb, Link2, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#A24936", "#5E7153", "#D4AF37"];
const emptyBook = { baslik: "", yazar: "", yayinevi: "", kunye_bilgisi: "", ozet_abstract: "", etiketler: "", zorluk_seviyesi: "Orta", kapak_resmi: "" };

export default function Admin() {
  const { user, loadGlossary } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || (user?.role === "admin" ? "analiz" : "kitaplar");
  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">Yönetim</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1 mb-6">Kontrol Paneli</h1>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex-wrap h-auto bg-surface-alt">
          {isAdmin && <TabsTrigger value="analiz" data-testid="tab-analiz">Analiz</TabsTrigger>}
          {isAdmin && <TabsTrigger value="uyeler" data-testid="tab-uyeler">Üyeler</TabsTrigger>}
          <TabsTrigger value="oneriler" data-testid="tab-oneriler">Öneriler</TabsTrigger>
          <TabsTrigger value="kitaplar" data-testid="tab-kitaplar">Kitaplar</TabsTrigger>
          <TabsTrigger value="sozluk" data-testid="tab-sozluk">Sözlük</TabsTrigger>
          <TabsTrigger value="listeler" data-testid="tab-listeler">Listeler</TabsTrigger>
          {isAdmin && <TabsTrigger value="duyuru" data-testid="tab-duyuru">Duyuru</TabsTrigger>}
          {isAdmin && <TabsTrigger value="bakim" data-testid="tab-bakim">Bakım</TabsTrigger>}
        </TabsList>
        {isAdmin && <TabsContent value="analiz"><Analytics /></TabsContent>}
        {isAdmin && <TabsContent value="uyeler"><UsersTab /></TabsContent>}
        <TabsContent value="oneriler"><SuggestionsTab /></TabsContent>
        <TabsContent value="kitaplar"><BooksTab /></TabsContent>
        <TabsContent value="sozluk"><GlossaryTab onChange={loadGlossary} /></TabsContent>
        <TabsContent value="listeler"><ListsTab /></TabsContent>
        {isAdmin && <TabsContent value="duyuru"><AnnouncementTab /></TabsContent>}
        {isAdmin && <TabsContent value="bakim"><MaintenanceTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <p className="text-muted-foreground py-6">Yükleniyor...</p>;
  const k = data.kartlar;
  const cards = [
    { label: "Toplam Üye", value: k.toplam_uye, icon: Users }, { label: "Onaylı Üye", value: k.onayli_uye, icon: Users },
    { label: "Bekleyen Üye", value: k.bekleyen_uye, icon: Users }, { label: "Toplam Kitap", value: k.toplam_kitap, icon: BookOpen },
    { label: "Okunan Kitap", value: k.okunan_kitap, icon: BookOpen }, { label: "Toplam Paylaşım", value: k.toplam_paylasim, icon: MessageSquare },
    { label: "Bekleyen Öneri", value: k.bekleyen_oneri, icon: Lightbulb },
  ];
  return (
    <div className="py-6 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="border border-line rounded-xl p-5 bg-surface" data-testid={`analytics-${c.label}`}>
            <c.icon className="text-terra" size={20} />
            <p className="font-serif text-3xl text-ink mt-2">{c.value}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-line rounded-xl p-5 bg-surface">
          <h3 className="font-serif text-xl text-ink mb-4">Aylık Aktivite</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.aylik_aktivite}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DC" />
              <XAxis dataKey="ay" stroke="#6B6965" fontSize={12} /><YAxis stroke="#6B6965" fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="okuma" stroke="#A24936" strokeWidth={2} name="Okuma" />
              <Line type="monotone" dataKey="etkilesim" stroke="#5E7153" strokeWidth={2} name="Etkileşim" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-line rounded-xl p-5 bg-surface">
          <h3 className="font-serif text-xl text-ink mb-4">Zorluk Dağılımı</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.zorluk_dagilimi} dataKey="sayi" nameKey="seviye" cx="50%" cy="50%" outerRadius={80}
                label={({ seviye, sayi }) => `${seviye}: ${sayi}`}>
                {data.zorluk_dagilimi.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {data.en_cok_okunan.length > 0 && (
        <div className="border border-line rounded-xl p-5 bg-surface">
          <h3 className="font-serif text-xl text-ink mb-4">En Çok Okunan 5 Kitap</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.en_cok_okunan}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DC" />
              <XAxis dataKey="baslik" stroke="#6B6965" fontSize={11} /><YAxis stroke="#6B6965" fontSize={12} />
              <Tooltip /><Bar dataKey="sayi" fill="#A24936" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn) => { try { await fn(); toast.success("Güncellendi."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resetAll = async () => {
    if (!window.confirm("TÜM üyelerin AktifPuanı kalıcı olarak sıfırlanacak. Emin misiniz?")) return;
    await act(() => api.post("/admin/ap/reset-all"));
  };
  const adjustAp = async (u) => {
    const val = window.prompt(`${u.ad_soyad} için AP değişikliği:\n• "+50" veya "-30" → ekle/çıkar\n• "=500" → tam olarak ayarla`, "");
    if (val === null) return;
    let mode = "add", amount;
    if (val.trim().startsWith("=")) { mode = "set"; amount = parseInt(val.trim().slice(1)); } else { amount = parseInt(val); }
    if (isNaN(amount)) { toast.error("Geçerli bir sayı girin."); return; }
    if (!window.confirm(`${u.ad_soyad}: ${mode === "set" ? "= " : (amount >= 0 ? "+" : "")}${amount} AP. Emin misiniz?`)) return;
    await act(() => api.post(`/admin/users/${u.id}/ap`, { amount, mode }));
  };
  const statusRenk = { approved: "text-sage", pending: "text-gold", rejected: "text-destructive" };
  const statusTr = { approved: "Onaylı", pending: "Beklemede", rejected: "Reddedildi" };
  return (
    <div className="py-6 overflow-x-auto">
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="outline" data-testid="reset-all-ap" onClick={resetAll} className="border-line text-destructive hover:bg-destructive/10">Tüm AP'yi Sıfırla</Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Ad Soyad</TableHead><TableHead>E-posta</TableHead><TableHead>Rol</TableHead><TableHead>Durum</TableHead><TableHead className="text-right">İşlemler</TableHead></TableRow></TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium text-ink">{u.ad_soyad}</TableCell>
              <TableCell className="text-muted-foreground">{u.email}</TableCell>
              <TableCell>
                <Select value={u.role} onValueChange={(v) => act(() => api.post(`/admin/users/${u.id}/role`, { rol: v }))}>
                  <SelectTrigger className="w-32 h-8" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="member">Üye</SelectItem><SelectItem value="moderator">Moderatör</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </TableCell>
              <TableCell className={statusRenk[u.status]}>{statusTr[u.status] || u.status}</TableCell>
              <TableCell className="text-right space-x-1">
                {u.status !== "approved" && <Button size="sm" variant="ghost" data-testid={`approve-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/approve`))} className="text-sage"><Check size={16} /></Button>}
                {u.status !== "rejected" && <Button size="sm" variant="ghost" data-testid={`reject-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/reject`))} className="text-gold"><X size={16} /></Button>}
                <Button size="sm" variant="ghost" data-testid={`ap-${u.id}`} onClick={() => adjustAp(u)} className="text-terra text-xs">AP</Button>
                <Button size="sm" variant="ghost" data-testid={`del-user-${u.id}`} onClick={() => act(() => api.delete(`/admin/users/${u.id}`))} className="text-destructive"><Trash2 size={16} /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SuggestionsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/suggestions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn) => { try { await fn(); toast.success("İşlem tamam."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const renk = { Beklemede: "text-gold", Onaylandı: "text-sage", Reddedildi: "text-destructive" };
  return (
    <div className="py-6 space-y-3">
      {list.map((s) => (
        <div key={s.id} className="border border-line rounded-lg p-4 bg-surface flex items-center justify-between" data-testid={`adm-sug-${s.id}`}>
          <div>
            <p className="font-medium text-ink">{s.kitap_adi} <span className="text-muted-foreground font-normal">— {s.yazar}</span></p>
            <p className="text-sm text-muted-foreground">{s.kullanici_adi} · <span className={renk[s.durum]}>{s.durum}</span></p>
            {s.not_ && <p className="text-sm text-ink-2 mt-1 italic">"{s.not_}"</p>}
          </div>
          {s.durum === "Beklemede" && (
            <div className="flex gap-2">
              <Button size="sm" data-testid={`sug-approve-${s.id}`} onClick={() => act(() => api.post(`/admin/suggestions/${s.id}/approve`))} className="bg-sage hover:bg-sage/90 text-white gap-1"><Check size={15} /> Kütüphaneye Ekle</Button>
              <Button size="sm" variant="outline" data-testid={`sug-reject-${s.id}`} onClick={() => act(() => api.post(`/admin/suggestions/${s.id}/reject`))} className="border-line text-destructive">Reddet</Button>
            </div>
          )}
        </div>
      ))}
      {list.length === 0 && <p className="text-muted-foreground text-sm">Öneri yok.</p>}
    </div>
  );
}

function BooksTab() {
  const [books, setBooks] = useState([]);
  const [form, setForm] = useState(emptyBook);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [imported, setImported] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const load = () => api.get("/books").then((r) => setBooks(r.data));
  useEffect(() => { load(); }, []);

  const handleImportUrl = async () => {
    if (!importUrl.trim()) return;
    setImporting(true); setImportError("");
    try {
      const { data } = await api.post("/admin/books/import-url", { url: importUrl });
      const list = data.books || [];
      if (list.length === 0) { setImportError("Bu bağlantıdan kitap çıkarılamadı."); return; }
      if (list.length === 1) {
        setForm({ ...emptyBook, ...list[0], etiketler: (list[0].etiketler || []).join(", ") });
        setEditId(null); setOpen(true); setImportUrl("");
        toast.success("Kitap bilgileri çıkarıldı. Kontrol edip kaydedin.");
      } else {
        setImported(list.map((b) => ({ ...b, _sel: true })));
        setImportOpen(true); setImportUrl("");
        toast.success(`${list.length} kitap bulundu. Eklemek istediklerini seç.`);
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "İçe aktarma başarısız oldu.";
      setImportError(msg); toast.error(msg);
    } finally { setImporting(false); }
  };

  const saveBulk = async () => {
    const sel = imported.filter((b) => b._sel);
    if (sel.length === 0) { toast.error("En az bir kitap seç."); return; }
    setSavingBulk(true);
    try {
      const { data } = await api.post("/admin/books/bulk-create", { books: sel });
      toast.success(`${data.eklenen} kitap eklendi.`);
      setImportOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingBulk(false); }
  };

  const downloadTemplate = () => {
    const csv = "\ufeffbaslik,yazar,yayinevi,etiketler,zorluk_seviyesi,ozet_abstract,kapak_resmi\n" +
      "Örnek Kitap,Örnek Yazar,Örnek Yayınevi,\"Stoacılık, Etik\",Orta,Kitabın kısa özeti,https://ornek.com/kapak.jpg\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "aktifokur_kitap_sablonu.csv"; a.click();
  };

  const openNew = () => { setForm(emptyBook); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...b, etiketler: (b.etiketler || []).join(", ") }); setEditId(b.id); setOpen(true); };

  const save = async () => {
    const payload = { ...form, etiketler: form.etiketler ? form.etiketler.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (editId) await api.put(`/books/${editId}`, payload); else await api.post("/books", payload);
      toast.success("Kaydedildi."); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const genAbstract = async () => {
    if (!form.baslik || !form.yazar) { toast.error("Önce başlık ve yazar girin."); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/books/generate-abstract", { baslik: form.baslik, yazar: form.yazar });
      setForm((f) => ({ ...f, ozet_abstract: data.abstract }));
      toast.success("Abstract oluşturuldu.");
    } catch (e) { toast.error("Oluşturulamadı."); } finally { setGenLoading(false); }
  };

  const genMeta = async () => {
    if (!form.baslik || !form.yazar) { toast.error("Önce başlık ve yazar girin."); return; }
    setMetaLoading(true);
    try {
      const { data } = await api.post("/books/generate-meta", { baslik: form.baslik, yazar: form.yazar });
      setForm((f) => ({ ...f, zorluk_seviyesi: data.zorluk_seviyesi, etiketler: (data.etiketler || []).join(", ") }));
      toast.success("Zorluk ve etiketler önerildi.");
    } catch (e) { toast.error("Oluşturulamadı."); } finally { setMetaLoading(false); }
  };

  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/books/${id}`); toast.success("Silindi."); load(); };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/books/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(data); toast.success(`${data.eklenen} kitap eklendi.`); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  return (
    <div className="py-6">
      <div className="border border-dashed border-line rounded-lg p-3 bg-surface-alt/40 mb-4 flex flex-wrap items-center gap-3" data-testid="url-import-bar">
        <Link2 size={18} className="text-terra shrink-0" />
        <div className="shrink-0">
          <p className="text-sm font-medium text-ink">Bağlantıdan İçe Aktar</p>
          <p className="text-xs text-muted-foreground">Amazon vb. kitap sayfası linkini yapıştır — AI bilgileri çıkarsın.</p>
        </div>
        <Input data-testid="import-url-input" value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
          placeholder="https://www.amazon.com/dp/..." className="flex-1 min-w-[220px] bg-surface" />
        <Button onClick={handleImportUrl} disabled={importing} data-testid="import-url-btn"
          className="bg-ink hover:bg-ink-2 text-page gap-1"><Sparkles size={15} /> {importing ? "Getiriliyor..." : "İçe Aktar"}</Button>
        {importError && <p className="w-full text-sm text-destructive mt-1" data-testid="import-url-error">{importError}</p>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="border border-dashed border-line rounded-lg p-3 bg-surface-alt/40 flex items-center gap-3">
          <Upload size={18} className="text-terra" />
          <div>
            <p className="text-sm font-medium text-ink">Toplu CSV İçe Aktar</p>
            <p className="text-xs text-muted-foreground">Sütunlar: baslik, yazar, yayinevi, etiketler, zorluk_seviyesi, ozet_abstract, kapak_resmi</p>
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="csv-input" />
            <span className="inline-block bg-ink text-page text-sm px-4 py-2 rounded-md">Dosya Seç</span>
          </label>
          <Button onClick={downloadTemplate} data-testid="csv-template" variant="outline" size="sm" className="border-line gap-1"><Download size={14} /> Boş Şablon İndir</Button>
        </div>
        <Button onClick={openNew} data-testid="add-book" className="bg-terra hover:bg-terra-hover text-white gap-1"><Plus size={16} /> Kitap Ekle</Button>
      </div>
      {csvResult && <p className="text-sm text-sage mb-3">{csvResult.eklenen} kitap eklendi. {csvResult.hatalar?.length ? `Hatalar: ${csvResult.hatalar.join("; ")}` : ""}</p>}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Başlık</TableHead><TableHead>Yazar</TableHead><TableHead>Etiketler</TableHead><TableHead>Zorluk</TableHead><TableHead className="text-right">İşlem</TableHead></TableRow></TableHeader>
          <TableBody>
            {books.map((b) => (
              <TableRow key={b.id} data-testid={`adm-book-${b.id}`}>
                <TableCell className="font-medium text-ink">{b.baslik}</TableCell>
                <TableCell className="text-muted-foreground">{b.yazar}</TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[200px]">{(b.etiketler || []).join(", ")}</TableCell>
                <TableCell>{b.zorluk_seviyesi}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" data-testid={`edit-book-${b.id}`} onClick={() => openEdit(b)}>Düzenle</Button>
                  <Button size="sm" variant="ghost" data-testid={`del-book-${b.id}`} onClick={() => del(b.id)} className="text-destructive"><Trash2 size={15} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Kitabı Düzenle" : "Yeni Kitap"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlık</Label><Input data-testid="bk-title" value={form.baslik} onChange={(e) => setForm({ ...form, baslik: e.target.value })} className="mt-1" /></div>
              <div><Label>Yazar</Label><Input data-testid="bk-author" value={form.yazar} onChange={(e) => setForm({ ...form, yazar: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yayınevi</Label><Input value={form.yayinevi} onChange={(e) => setForm({ ...form, yayinevi: e.target.value })} className="mt-1" /></div>
              <div><Label>Zorluk</Label>
                <Select value={form.zorluk_seviyesi} onValueChange={(v) => setForm({ ...form, zorluk_seviyesi: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Başlangıç">Başlangıç</SelectItem><SelectItem value="Orta">Orta</SelectItem><SelectItem value="İleri">İleri</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between"><Label>Etiketler (virgülle)</Label>
                <Button type="button" size="sm" variant="ghost" data-testid="gen-meta" onClick={genMeta} disabled={metaLoading} className="text-terra gap-1 h-7"><Sparkles size={14} /> {metaLoading ? "..." : "AI ile Öner (Zorluk+Etiket)"}</Button>
              </div>
              <Input data-testid="bk-tags" value={form.etiketler} onChange={(e) => setForm({ ...form, etiketler: e.target.value })} className="mt-1" placeholder="Stoacılık, Etik" />
            </div>
            <div><Label>Kapak Resmi URL</Label><Input value={form.kapak_resmi} onChange={(e) => setForm({ ...form, kapak_resmi: e.target.value })} className="mt-1" /></div>
            <div>
              <div className="flex items-center justify-between"><Label>Abstract</Label>
                <Button type="button" size="sm" variant="ghost" data-testid="gen-abstract" onClick={genAbstract} disabled={genLoading} className="text-terra gap-1 h-7"><Sparkles size={14} /> {genLoading ? "..." : "AI ile Oluştur"}</Button>
              </div>
              <Textarea data-testid="bk-abstract" value={form.ozet_abstract} onChange={(e) => setForm({ ...form, ozet_abstract: e.target.value })} className="mt-1" rows={4} />
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-book" className="bg-terra hover:bg-terra-hover text-white">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-surface max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Bağlantıdan Bulunan Kitaplar</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Eklemek istediklerini seç ({imported.filter((b) => b._sel).length}/{imported.length}).</p>
          <div className="space-y-2 my-3">
            {imported.map((b, i) => (
              <label key={i} className="flex items-start gap-3 border border-line rounded-md p-3 cursor-pointer hover:bg-surface-alt" data-testid={`imp-book-${i}`}>
                <input type="checkbox" checked={b._sel} className="mt-1"
                  onChange={() => setImported(imported.map((x, j) => j === i ? { ...x, _sel: !x._sel } : x))} />
                <div className="flex-1">
                  <p className="font-medium text-ink text-sm">{b.baslik} <span className="text-muted-foreground font-normal">— {b.yazar || "?"}</span></p>
                  <p className="text-xs text-muted-foreground">{b.zorluk_seviyesi}{b.etiketler?.length ? ` · ${b.etiketler.join(", ")}` : ""}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={saveBulk} disabled={savingBulk} data-testid="save-bulk" className="bg-terra hover:bg-terra-hover text-white">{savingBulk ? "Ekleniyor..." : "Seçilenleri Ekle"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GlossaryTab({ onChange }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ kavram: "", tanim: "" });
  const [editId, setEditId] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [sugs, setSugs] = useState([]);
  const load = () => {
    api.get("/glossary").then((r) => { setList(r.data); onChange?.(); });
    api.get("/admin/glossary-suggestions").then((r) => setSugs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const actSug = async (fn) => { try { await fn(); toast.success("İşlem tamam."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const bekleyen = sugs.filter((s) => s.durum === "Beklemede");
  const renk = { Beklemede: "text-gold", Onaylandı: "text-sage", Reddedildi: "text-destructive" };

  const save = async () => {
    try {
      if (editId) await api.put(`/glossary/${editId}`, form); else await api.post("/glossary", form);
      setForm({ kavram: "", tanim: "" }); setEditId(null); toast.success("Kaydedildi."); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const gen = async () => {
    if (!form.kavram) { toast.error("Kavram girin."); return; }
    setGenLoading(true);
    try { const { data } = await api.post("/glossary/generate-definition", { kavram: form.kavram }); setForm((f) => ({ ...f, tanim: data.tanim })); }
    catch (e) { toast.error("Oluşturulamadı."); } finally { setGenLoading(false); }
  };
  const del = async (id) => { await api.delete(`/glossary/${id}`); load(); };

  return (
    <div className="py-6 space-y-6">
      {bekleyen.length > 0 && (
        <div className="border border-gold/40 rounded-xl p-5 bg-gold/5" data-testid="glossary-suggestions">
          <h3 className="font-serif text-xl text-ink mb-3">Bekleyen Üye Önerileri ({bekleyen.length})</h3>
          <div className="space-y-2">
            {bekleyen.map((s) => (
              <div key={s.id} className="border border-line rounded-lg p-4 bg-surface flex items-start justify-between gap-3" data-testid={`gl-sug-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-terra">{s.kavram}</p>
                  <p className="text-sm text-ink-2">{s.tanim}</p>
                  <p className="text-xs text-muted-foreground mt-1">Öneren: {s.kullanici_adi}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" data-testid={`gl-sug-approve-${s.id}`} onClick={() => actSug(() => api.post(`/admin/glossary-suggestions/${s.id}/approve`))} className="bg-sage hover:bg-sage/90 text-white gap-1"><Check size={15} /> Onayla</Button>
                  <Button size="sm" variant="outline" data-testid={`gl-sug-reject-${s.id}`} onClick={() => actSug(() => api.post(`/admin/glossary-suggestions/${s.id}/reject`))} className="border-line text-destructive">Reddet</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6">
      <div className="border border-line rounded-xl p-5 bg-surface h-fit">
        <h3 className="font-serif text-xl text-ink mb-3">{editId ? "Kavramı Düzenle" : "Yeni Kavram"}</h3>
        <div className="space-y-3">
          <div><Label>Kavram</Label><Input data-testid="gl-term" value={form.kavram} onChange={(e) => setForm({ ...form, kavram: e.target.value })} className="mt-1" /></div>
          <div>
            <div className="flex items-center justify-between"><Label>Tanım</Label>
              <Button type="button" size="sm" variant="ghost" data-testid="gen-definition" onClick={gen} disabled={genLoading} className="text-terra gap-1 h-7"><Sparkles size={14} /> {genLoading ? "..." : "AI ile Oluştur"}</Button>
            </div>
            <Textarea data-testid="gl-def" value={form.tanim} onChange={(e) => setForm({ ...form, tanim: e.target.value })} className="mt-1" rows={4} />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="save-glossary" className="bg-terra hover:bg-terra-hover text-white flex-1">Kaydet</Button>
            {editId && <Button variant="outline" onClick={() => { setForm({ kavram: "", tanim: "" }); setEditId(null); }} className="border-line">İptal</Button>}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {list.map((g) => (
          <div key={g.id} className="border border-line rounded-lg p-4 bg-surface flex items-start justify-between" data-testid={`adm-gl-${g.id}`}>
            <div className="flex-1"><p className="font-serif text-lg text-terra">{g.kavram}</p><p className="text-sm text-ink-2">{g.tanim}</p></div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => { setForm(g); setEditId(g.id); }}>Düzenle</Button>
              <Button size="sm" variant="ghost" onClick={() => del(g.id)} className="text-destructive"><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function MaintenanceTab() {
  const [dups, setDups] = useState([]);
  const [tags, setTags] = useState([]);
  const load = () => { api.get("/admin/duplicate-books").then((r) => setDups(r.data)); api.get("/admin/similar-tags").then((r) => setTags(r.data)); };
  useEffect(() => { load(); }, []);
  const mergeBooks = async (g, keep) => {
    const remove = g.kitaplar.filter((b) => b.id !== keep.id).map((b) => b.id);
    if (!window.confirm(`${remove.length} kopya, "${keep.baslik}" ile birleştirilecek. Emin misiniz?`)) return;
    try { const { data } = await api.post("/admin/books/merge", { keep_id: keep.id, remove_ids: remove }); toast.success(`${data.birlestirilen} kitap birleştirildi.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const mergeTags = async (g) => {
    if (!window.confirm(`Etiketler "${g.onerilen}" altında birleştirilecek. Emin misiniz?`)) return;
    try { const { data } = await api.post("/admin/tags/merge", { from_tags: g.etiketler, to_tag: g.onerilen }); toast.success(`${data.guncellenen} kitap güncellendi.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="py-6 grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="font-serif text-xl text-ink mb-3">Olası Kopya Kitaplar ({dups.length})</h3>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {dups.length === 0 && <p className="text-sm text-muted-foreground">Kopya bulunamadı.</p>}
          {dups.map((g, i) => (
            <div key={i} className="border border-line rounded-lg p-3 bg-surface" data-testid={`dup-group-${i}`}>
              <p className="text-sm font-medium text-ink mb-2">{g.baslik}</p>
              {g.kitaplar.map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm py-1 gap-2">
                  <span className="text-ink-2 truncate">{b.yazar || "— yazar yok —"}</span>
                  <Button size="sm" variant="outline" data-testid={`merge-keep-${b.id}`} onClick={() => mergeBooks(g, b)} className="border-line h-7 text-xs shrink-0">Bunu koru</Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-xl text-ink mb-3">Benzer Etiketler ({tags.length})</h3>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {tags.length === 0 && <p className="text-sm text-muted-foreground">Benzer etiket yok.</p>}
          {tags.map((g, i) => (
            <div key={i} className="border border-line rounded-lg p-3 bg-surface flex items-center justify-between gap-2" data-testid={`tag-group-${i}`}>
              <div className="min-w-0"><p className="text-sm text-ink truncate">{g.etiketler.join(" · ")}</p><p className="text-xs text-muted-foreground">→ {g.onerilen}</p></div>
              <Button size="sm" variant="outline" data-testid={`merge-tag-${i}`} onClick={() => mergeTags(g)} className="border-line h-7 text-xs shrink-0">Birleştir</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnnouncementTab() {
  const [mesaj, setMesaj] = useState("");
  const [sure, setSure] = useState("24");
  const [mevcut, setMevcut] = useState(null);
  const [aktif, setAktif] = useState(false);
  const load = () => api.get("/admin/announcement").then((r) => { setMevcut(r.data.mevcut); setAktif(r.data.aktif_mi); if (r.data.mevcut?.mesaj) setMesaj(r.data.mevcut.mesaj); });
  useEffect(() => { load(); }, []);

  const yayinla = async () => {
    if (!mesaj.trim()) { toast.error("Mesaj boş olamaz."); return; }
    try { await api.post("/admin/announcement", { mesaj, sure_saat: parseInt(sure, 10) }); toast.success("Duyuru yayınlandı."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const kaldir = async () => { await api.delete("/admin/announcement"); setMesaj(""); toast.success("Duyuru kaldırıldı."); load(); };

  return (
    <div className="py-6 max-w-2xl">
      <div className="border border-line rounded-xl p-5 bg-surface">
        <h3 className="font-serif text-xl text-ink mb-1">Panel Duyurusu</h3>
        <p className="text-sm text-muted-foreground mb-4">Tüm üyelerin panelinde (orta sütun en üstünde) belirlenen süre boyunca görünür.</p>
        {mevcut && (
          <div className={`mb-4 rounded-lg p-3 text-sm border ${aktif ? "border-sage/40 bg-sage/10" : "border-line bg-surface-alt/50"}`} data-testid="announcement-status">
            <p className="font-medium text-ink">{aktif ? "🟢 Şu an yayında" : "⚪ Süresi doldu / pasif"}</p>
            <p className="text-ink-2 mt-1">{mevcut.mesaj}</p>
            {mevcut.bitis && <p className="text-xs text-muted-foreground mt-1">Bitiş: {new Date(mevcut.bitis).toLocaleString("tr-TR")}</p>}
          </div>
        )}
        <Label>Duyuru Mesajı</Label>
        <Textarea data-testid="announcement-message" value={mesaj} onChange={(e) => setMesaj(e.target.value)} rows={3} className="mt-1 mb-3" placeholder="Örn: Bu ayın okuma kulübü kitabı belirlendi!" />
        <Label>Yayında Kalma Süresi</Label>
        <Select value={sure} onValueChange={setSure}>
          <SelectTrigger className="mt-1 mb-4" data-testid="announcement-duration"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 saat</SelectItem>
            <SelectItem value="6">6 saat</SelectItem>
            <SelectItem value="24">1 gün</SelectItem>
            <SelectItem value="72">3 gün</SelectItem>
            <SelectItem value="168">1 hafta</SelectItem>
            <SelectItem value="720">1 ay</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button onClick={yayinla} data-testid="announcement-publish" className="bg-terra hover:bg-terra-hover text-white flex-1">Yayınla</Button>
          {mevcut && <Button onClick={kaldir} data-testid="announcement-clear" variant="outline" className="border-line text-destructive">Kaldır</Button>}
        </div>
      </div>
    </div>
  );
}

function ListsTab() {
  const [lists, setLists] = useState([]);
  const [books, setBooks] = useState([]);
  const [form, setForm] = useState({ liste_adi: "", aciklama: "", tur: "Tematik Listeler", kitap_id_listesi: [] });
  const [editId, setEditId] = useState(null);
  const [bookQ, setBookQ] = useState("");
  const load = () => { api.get("/reading-lists").then((r) => setLists(r.data)); api.get("/books").then((r) => setBooks(r.data)); };
  useEffect(() => { load(); }, []);

  const toggleBook = (id) => setForm((f) => ({ ...f, kitap_id_listesi: f.kitap_id_listesi.includes(id) ? f.kitap_id_listesi.filter((x) => x !== id) : [...f.kitap_id_listesi, id] }));
  const save = async () => {
    try {
      if (editId) await api.put(`/reading-lists/${editId}`, form); else await api.post("/reading-lists", form);
      setForm({ liste_adi: "", aciklama: "", tur: "Tematik Listeler", kitap_id_listesi: [] }); setEditId(null); toast.success("Kaydedildi."); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/reading-lists/${id}`); load(); };

  return (
    <div className="py-6 grid lg:grid-cols-[1fr_1fr] gap-6">
      <div className="border border-line rounded-xl p-5 bg-surface h-fit">
        <h3 className="font-serif text-xl text-ink mb-3">{editId ? "Listeyi Düzenle" : "Yeni Liste"}</h3>
        <div className="space-y-3">
          <div><Label>Liste Adı</Label><Input data-testid="list-name" value={form.liste_adi} onChange={(e) => setForm({ ...form, liste_adi: e.target.value })} className="mt-1" /></div>
          <div><Label>Açıklama</Label><Textarea value={form.aciklama} onChange={(e) => setForm({ ...form, aciklama: e.target.value })} className="mt-1" rows={2} /></div>
          <div><Label>Tür</Label>
            <Select value={form.tur} onValueChange={(v) => setForm({ ...form, tur: v })}>
              <SelectTrigger className="mt-1" data-testid="list-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Tematik Listeler">Tematik Listeler</SelectItem><SelectItem value="Aylık Okuma Kulübü">Aylık Okuma Kulübü</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kitaplar (yaz ve seç)</Label>
            <Input data-testid="list-book-search" value={bookQ} onChange={(e) => setBookQ(e.target.value)} placeholder="Kitap adı veya yazar ara..." className="mt-1 mb-2" />
            <div className="mt-1 max-h-52 overflow-y-auto border border-line rounded-md p-2 space-y-1">
              {books.filter((b) => !bookQ.trim() || `${b.baslik} ${b.yazar}`.toLowerCase().includes(bookQ.toLowerCase())).slice(0, 100).map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm p-1 hover:bg-surface-alt rounded cursor-pointer">
                  <input type="checkbox" checked={form.kitap_id_listesi.includes(b.id)} onChange={() => toggleBook(b.id)} />
                  {b.baslik} <span className="text-muted-foreground">— {b.yazar}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{form.kitap_id_listesi.length} kitap seçili</p>
          </div>
          <Button onClick={save} data-testid="save-list" className="bg-terra hover:bg-terra-hover text-white w-full">Kaydet</Button>
        </div>
      </div>
      <div className="space-y-2">
        {lists.map((l) => (
          <div key={l.id} className="border border-line rounded-lg p-4 bg-surface flex items-start justify-between" data-testid={`adm-list-${l.id}`}>
            <div><p className="font-medium text-ink">{l.liste_adi}</p><p className="text-xs text-muted-foreground">{l.tur} · {l.kitaplar?.length || 0} kitap</p></div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setForm({ liste_adi: l.liste_adi, aciklama: l.aciklama, tur: l.tur, kitap_id_listesi: l.kitap_id_listesi || [] }); setEditId(l.id); }}>Düzenle</Button>
              <Button size="sm" variant="ghost" onClick={() => del(l.id)} className="text-destructive"><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
